import { CodeHighlight } from '@mantine/code-highlight';
import { Button, Stack, Text } from '@mantine/core';
import { modals } from '@mantine/modals';
import { IconKey } from '@tabler/icons-react';
import { createFileRoute, LoaderFnContext, useRouter } from '@tanstack/react-router';
import { notifyError } from '@/lib/notification';
import { PageHeader } from '@/components/PageHeader';
import { MyRouterContext } from '@/App';
import { isPersonalTenant } from '@/lib/auth';
import { logger } from '@/lib/logger';
import { suppress404 } from '@/lib/zodius';
import { fmtDate } from '@/lib/date';
import { LabeledValue } from '@/components/LabeledValue';
import { MainContainer } from '@/components/MainContainer';
import { PendingInitialize } from '@/components/PendingInitialize';
import { useState } from 'react';

export const Route = createFileRoute('/_main/_project/profile/')({
  loader,
  component: Component,
  pendingComponent: () => <PendingInitialize title="プロファイル" />,
});

async function loader({
  context: { adminClient },
}: LoaderFnContext<Record<never, string>, never, MyRouterContext>) {
  const user = await adminClient.settings_v1_get_user_profile();
  const apiKey = await suppress404(adminClient.settings_v1_get_personal_api_key());
  return {
    user,
    apiKey,
    adminClient,
  };
}

function Component() {
  const { user, apiKey, adminClient } = Route.useLoaderData();
  const { userInfo } = Route.useRouteContext();
  const isPersonal = isPersonalTenant(userInfo);
  const router = useRouter();
  const [loading, setLoading] = useState(false);

  // handlers
  const handleGenerateApikey = async () => {
    setLoading(true);
    try {
      const res = await adminClient.settings_v1_generate_personal_api_key(undefined);
      await router.load();
      openGenerateApiKeyResult(res.api_key);
    } catch (e) {
      logger.error(e);
      notifyError('シークレットの発行に失敗しました。');
    } finally {
      setLoading(false);
    }
  };

  // modals
  const openConfirmRegenerateApiKey = () => {
    modals.openConfirmModal({
      title: 'APIキーのシークレットを再発行しますか？',
      children: (
        <div className="text-sm">
          この操作により新しいシークレットが作成され、現在のシークレットは無効になります。
        </div>
      ),
      onConfirm: () => handleGenerateApikey(),
    });
  };

  const openGenerateApiKeyResult = (secret: string) => {
    modals.open({
      title: 'APIキーのシークレットが発行されました',
      children: (
        <>
          <div className="text-sm">
            シークレットは、この画面を閉じると再表示できません。安全な場所に保存してください。
          </div>
          <div className="my-10">
            <div className="text-sm mb-1">APIキーのシークレット</div>
            <CodeHighlight
              classNames={{ root: 'border border-plamo-border rounded-lg' }}
              code={secret}
            ></CodeHighlight>
          </div>
        </>
      ),
    });
  };

  return (
    <MainContainer>
      <PageHeader title="プロファイル" />
      <Stack my="md" maw={480} gap={48}>
        <Stack>
          <Text size="lg" fw="500">
            ユーザ情報
          </Text>
          <LabeledValue label="名前" value={user.name} />
          <LabeledValue label="Eメール" value={user.email} />
        </Stack>
        <Stack>
          <Text size="lg" fw="500">
            API キー
          </Text>
          <Text size="sm">
            {isPersonal
              ? 'PLaMo API を実行するときに使います。無料プランで使えるキーはこの 1 本です。'
              : // 有料テナントにはプロジェクトに紐づくキーがあり、そちらが本筋
                '個人 API キーは、プロジェクト単位での課金管理ができません。ユーザがテナントのメンバーから外れると自動的に無効化されます。本番環境のシステムに組み込む際には、プロジェクトに紐づく API キーの利用を推奨します。'}
          </Text>
          <LabeledValue
            label="シークレット"
            value={apiKey?.spec.masked_secret}
            emptyLabel="未発行"
          />
          {apiKey && (
            <LabeledValue
              label="作成日"
              value={fmtDate((apiKey.meta?.created_at as string) ?? null)}
            />
          )}
          <Button
            // 再発行は今のキーを無効にするので、プロジェクトの API キー一覧と同じく赤で出す
            color={apiKey ? 'red' : 'blue'}
            variant={apiKey ? 'outline' : 'filled'}
            size="sm"
            w="fit-content"
            leftSection={<IconKey style={{ width: '70%', height: '70%' }} />}
            onClick={async () => {
              if (apiKey) {
                openConfirmRegenerateApiKey();
              } else {
                await handleGenerateApikey();
              }
            }}
            loading={loading}
          >
            {apiKey ? 'API キーを再発行する' : 'API キーを発行する'}
          </Button>
        </Stack>
      </Stack>
    </MainContainer>
  );
}
